import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";

export type CoursePricingType = "free" | "paid";

export const DEFAULT_COURSE_CURRENCY = "EGP";

export type InstructorCourse = {
  id: number;
  title: string;
  description?: string | null;
  image?: string | null;
  image_url?: string | null;
  subject_id?: number;
  grade_id?: number | null;
  grade?: { id: number; name: string; stage_id?: number | null } | null;
  material_id?: number;
  created_by?: number;
  pricing_type?: CoursePricingType;
  is_free?: boolean;
  is_paid?: boolean;
  price?: number | null;
  currency?: string | null;
  created_at?: string;
  updated_at?: string;
  subject?: any;
  material?: any;
};

export type CoursesListQuery = {
  page?: number;
  limit?: number;
  term?: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc";
};

export type CourseUpsertBody = {
  title: string;
  description?: string | null;
  image?: string | null;
  subject_id: number;
  grade_id?: number | null;
  pricing_type?: CoursePricingType;
  price?: number | null;
  currency?: string | null;
};

/**
 * Resolve the effective pricing type for a course, falling back for legacy
 * records that predate the pricing_type column (price > 0 => paid).
 */
export function resolveCoursePricingType(course: Partial<InstructorCourse>): CoursePricingType {
  if (course.pricing_type === "free" || course.pricing_type === "paid") {
    return course.pricing_type;
  }
  return Number(course.price ?? 0) > 0 ? "paid" : "free";
}

/**
 * Build a clean pricing payload. Never emits empty strings or undefined keys:
 * free => price 0, paid => numeric price.
 */
export function buildPricingPayload(input: {
  pricingType: CoursePricingType;
  price: number | string | null;
  currency?: string | null;
}): { pricing_type: CoursePricingType; price: number; currency: string } {
  const currency = (input.currency || DEFAULT_COURSE_CURRENCY).trim() || DEFAULT_COURSE_CURRENCY;
  if (input.pricingType === "paid") {
    return {
      pricing_type: "paid",
      price: Number(input.price) || 0,
      currency,
    };
  }
  return { pricing_type: "free", price: 0, currency };
}

export const instructorCoursesService = {
  async list(params: CoursesListQuery = {}) {
    const res = await api.get<ApiEnvelope<PaginatedResult<InstructorCourse>> | ApiEnvelope<{ data: InstructorCourse[] }>>(
      "/instructors/courses",
      { params, meta: { toastSuccess: false } }
    );
    // prefer paginated if present
    const asPaginated = unwrapList<InstructorCourse>(res.data as any);
    if (Array.isArray(asPaginated.data) && (asPaginated.meta?.total ?? 0) >= 0) return asPaginated;

    const list = (res.data as any)?.result?.data;
    return {
      data: Array.isArray(list) ? list : [],
      meta: { current_page: 1, last_page: 1, per_page: params.limit ?? 10, total: Array.isArray(list) ? list.length : 0, from: null, to: null },
    } as PaginatedResult<InstructorCourse>;
  },

  async get(courseId: number | string) {
    const res = await api.get<ApiEnvelope<{ data: InstructorCourse }>>(
      `/instructors/courses/${courseId}`,
      { meta: { toastSuccess: false } }
    );
    return unwrapData(res.data);
  },

  async create(body: CourseUpsertBody) {
    const res = await api.post<ApiEnvelope<{ data: InstructorCourse }>>("/instructors/courses", body);
    // Be resilient to backend envelope variations:
    // { result: { data: course } } | { result: { course } } | { result: course }
    const result = (res.data as any)?.result;
    const course = result?.data ?? result?.course ?? result ?? null;
    return (course as InstructorCourse | null) ?? unwrapData(res.data);
  },

  async update(courseId: number | string, body: CourseUpsertBody) {
    const res = await api.put<ApiEnvelope<{ data: InstructorCourse }>>(`/instructors/courses/${courseId}`, body);
    return unwrapData(res.data);
  },

  async remove(courseId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(`/instructors/courses/${courseId}`);
    return { message: (res.data as any)?.message ?? "Deleted." };
  },
};
